















import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  BulletListNode,
  CommentHighlights,
  HardBreakNode,
  HeadingLevel,
  HeadingNode,
  ListItemNode,
  MergeValueNode,
  NodeAlign,
  OrderedListNode,
  ParagraphNode,
  ProsemirrorType,
  TextAlign,
  TextDocNode,
} from '@getaccept/editor-lib';
import type { ProseMirrorSelection } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import { SelectionType } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import type { TextContentDoc } from '@getaccept/lib-shared/src/types/text-content';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import type { EditorOptions } from '@tiptap/vue-2';
import { EditorContent, Editor as TipTapEditor } from '@tiptap/vue-2';
import type { PropType, Ref } from '@vue/composition-api';
import {
  toRef,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import { storeToRefs } from 'pinia';
import { useCreateContextualCommentButton } from '../composables/create-contextual-comment-button';
import { handleInit, setContent, updateMergeValues } from './helpers/prosemirror-viewer.helper';
import CreateCommentButton from './CreateCommentButton.vue';

export default defineComponent({
  name: 'ProsemirrorViewerText',
  components: {
    EditorContent,
    CreateCommentButton,
  },
  props: {
    defaultHeading: {
      type: Number as PropType<HeadingLevel>,
      default: () => HeadingLevel.Heading1,
    },
    defaultAlign: { type: String as PropType<NodeAlign>, default: () => NodeAlign.Left },
    content: {
      type: [Object, String] as PropType<TextContentDoc | string>,
      default: '',
    },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    showContextualComments: { type: Boolean, default: false },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
  },
  setup(props, { emit }) {
    const viewer: Ref<TipTapEditor | null> = ref(null);
    const editorContainer = ref(null);
    const isEditorReady = ref(false);

    const getCoordsAtSelectionEnd = () => {
      if (!viewer.value?.view) {
        return null;
      }

      const { to } = viewer.value.view.state.selection;
      return viewer.value.view.coordsAtPos(to);
    };

    const {
      showCreateCommentButton,
      calculatePosition: calculateCreateCommentButtonPosition,
      hideButton: hideCreateCommentButton,
      buttonPosition: createCommentButtonPosition,
    } = useCreateContextualCommentButton(
      toRef(props, 'showContextualComments'),
      editorContainer,
      getCoordsAtSelectionEnd
    );

    const { hoveredCommentId } = storeToRefs(useContextualCommentingStore());

    const canCreateComment = computed(
      () =>
        props.showContextualComments &&
        props.createContextualCommentButtonState === CreateContextualCommentButtonState.Enabled
    );

    const contentPayload = computed(() => ({
      viewer: viewer.value,
      content: props.content,
      fields: props.fields,
      mergeValues: props.mergeValues,
    }));

    const textViewer = computed<Partial<EditorOptions>>(() => ({
      content: props.content,
      editable: false,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        TextDocNode,
        ParagraphNode,
        Text,
        Bold,
        Italic,
        Strike,
        Superscript,
        Underline,
        OrderedListNode,
        CommentHighlights.configure({
          onCursorIntersection: (commentId: string) => {
            emit('select-comment', commentId);
          },
        }),
        BulletListNode,
        ListItemNode,
        MergeValueNode,
        Link.configure({
          autolink: false,
          openOnClick: true,
          linkOnPaste: false,
        }),
        HeadingNode.configure({
          defaultLevel: props.defaultHeading,
        }),
        HardBreakNode,
        TextAlign.configure({
          defaultAlignment: props.defaultAlign,
          types: [
            ProsemirrorType.Paragraph,
            ProsemirrorType.Heading,
            ProsemirrorType.BulletList,
            ProsemirrorType.OrderedList,
          ],
        }),
      ],
      onCreate: () => {
        handleInit(viewer.value, props.fields, props.mergeValues);
        isEditorReady.value = true;
      },
      onSelectionUpdate: calculateCreateCommentButtonPosition,
    }));

    const isShowingCommentHighlights = computed(
      () => !!props.comments.length && props.showContextualComments && isEditorReady.value
    );

    watch(
      () => props.comments,
      () => {
        viewer.value.commands.updateCommentHighlights(props.comments);
      }
    );

    watch(isShowingCommentHighlights, () => {
      if (isShowingCommentHighlights.value) {
        viewer.value.commands.showCommentHighlights(props.comments);
        return;
      }
      viewer.value.commands.hideCommentHighlights();
    });

    watch(
      () => props.selectedCommentId,
      () => {
        viewer.value.commands.selectCommentHighlight(props.selectedCommentId);
        hideCreateCommentButton();
      }
    );

    watch(hoveredCommentId, () =>
      viewer.value.commands.hoverCommentHighlight(hoveredCommentId.value)
    );

    const createComment = () => {
      const { selection } = viewer.value.view.state;
      const { head, anchor } = selection.toJSON();
      const editor = viewer.value;
      if (!editor) return;

      const content = JSON.stringify(
        editor.view.state.doc.cut(selection.from, selection.to).toJSON()
      );

      const textSelection: ProseMirrorSelection = {
        head,
        anchor,
        type: SelectionType.Text,
      };

      hideCreateCommentButton();

      emit('create-comment', {
        textSelection,
        content,
      });
    };

    onMounted(() => {
      viewer.value = new TipTapEditor(textViewer.value);
    });

    onBeforeUnmount(() => {
      viewer.value.destroy();
    });

    watch(
      () => [props.fields, props.mergeValues],
      () => {
        updateMergeValues(contentPayload.value);
      }
    );

    watch(
      () => props.content,
      () => {
        setContent(contentPayload.value);
      }
    );

    return {
      createComment,
      viewer,
      editorContainer,
      showCreateCommentButton,
      createCommentButtonPosition,
      canCreateComment,
    };
  },
});
